/* eslint-disable complexity */
import './styles.css';

import React, { forwardRef, Ref } from 'react';

import { ClOfferSnippet } from '@search/classified-common/src/offer/snippet/ClOfferSnippet';
import classname from '@search/classname/src';

const cn = classname.bind(null, 'LayoutSnippet');

interface IProps {
    borderEnabled?: boolean;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    renderBadges: React.ReactNode;
    renderGallery: React.ReactNode;
    renderTitle: React.ReactNode;
    renderBuilding?: React.ReactNode;
    renderMetro?: React.ReactNode;
    renderAddress?: React.ReactNode;
    renderDescription: React.ReactNode;
    renderPrice: React.ReactNode;
    renderNewPriceDetail: React.ReactNode;
    renderMortgageLink: React.ReactNode;
    renderMortgage?: React.ReactNode;
    renderModeratorExtension?: React.ReactNode;
    renderPaymentsInfo?: React.ReactNode;
    renderPhone: React.ReactNode;
    renderSeller: React.ReactNode;
    renderFlats?: React.ReactNode;
    renderLastUpdate: React.ReactNode;
    renderNewOnMap: React.ReactNode;
    renderMap?: React.ReactNode;
    renderNote?: React.ReactNode;
    renderOwnerInfo?: React.ReactNode;
    renderFavoritesButton: React.ReactNode;
    renderOnlinePurchase: React.ReactNode;
    renderValidate?: React.ReactNode;
    dataTest?: string;
    hasHighlightPriority?: boolean;
    hasTopPriority?: boolean;
    id?: string;
}

export const LayoutSnippet = forwardRef(({
    onMouseEnter,
    onMouseLeave,
    renderBadges,
    renderGallery,
    renderTitle,
    renderBuilding,
    renderMetro,
    renderAddress,
    renderDescription,
    renderFlats,
    renderPhone,
    renderSeller,
    renderLastUpdate,
    renderNewOnMap,
    renderPrice,
    renderNewPriceDetail,
    renderMortgageLink,
    renderMortgage,
    renderModeratorExtension,
    renderOnlinePurchase,
    renderValidate,
    renderMap,
    renderNote,
    renderPaymentsInfo,
    renderFavoritesButton,
    dataTest,
    hasHighlightPriority,
    hasTopPriority,
    id,
    renderOwnerInfo,
    borderEnabled
}: IProps, containerRef: Ref<HTMLDivElement>) => {
    const addElementAreaLink = (node: React.ReactNode): React.ReactNode => React.Children
        // @ts-ignore
        .map(node, (child: React.ReactElement) => {
            const props = {
                ...child.props,
                children: [
                    React.createElement('div', {
                        className: cn('areaLink'),
                        key: 'areaLink'
                    }),
                    child.props.children
                ]
            };

            return React.cloneElement(child, props);
        });

    const snippet = (<div className={cn('main')}>
        <div className={cn('body')}>
            <div className={cn('generalInfo')}>
                <div
                    className={cn('title')}
                    data-test={dataTest ? `${dataTest}-title` : undefined}
                >
                    {addElementAreaLink(renderTitle)}

                    {renderMetro && <div className={cn('metro')}>{renderMetro}</div>}
                    {renderAddress && <div className={cn('address')}>{renderAddress}</div>}
                    {renderFlats && <div className={cn('flats')}>{renderFlats}</div>}
                    <div className={cn('map')}>{renderNewOnMap}</div>
                    {renderMap}
                </div>
                <div className={cn('generalDescription')}>
                    <div className={cn('priceDescription')}>
                        {renderPrice && <div className={cn('price')}>{renderPrice}</div>}
                        {renderNewPriceDetail && (
                            <div className={cn('priceDetail')}>{renderNewPriceDetail}</div>
                        )}
                    </div>
                    {renderMortgage}
                    {renderMortgageLink}
                    <div className={cn('adaptive')}>
                        {renderOnlinePurchase}
                        {renderValidate}
                    </div>
                </div>
            </div>
            <div className={cn('dealInfo', { isOwner: Boolean(renderOwnerInfo) })}>
                <div className={cn('dealInfoTop')}>
                    {renderBuilding && <div className={cn('building')}>{renderBuilding}</div>}
                    <div className={cn('description')}>{renderDescription}</div>

                    {/* Описание аренды, список разделять через → $npsb; */}
                    {renderPaymentsInfo && (
                        <div className={cn('paymentsInfo')}>{renderPaymentsInfo}</div>
                    )}

                    {/* Расширение для модерации */}
                    {renderModeratorExtension && <div className={cn('moderatorExtension')}>
                        {renderModeratorExtension}
                    </div>}
                    <div className={cn('lastUpdate')}>{renderLastUpdate}</div>
                </div>
                <div className={cn('ownerInfo', { owner: Boolean(renderOwnerInfo) })}>
                    <div className={cn('seller')}>
                        {renderOwnerInfo || renderSeller}
                    </div>
                    {renderNote}
                    {renderOwnerInfo ? null : (
                        <div className={cn('showPhone')}>{renderPhone}</div>
                    )}
                </div>
            </div>
            {renderFavoritesButton && (
                <div className={cn('favorites-button-container')}>
                    {renderFavoritesButton}
                </div>
            )}
        </div>
    </div>);

    return (
        <ClOfferSnippet
            border={borderEnabled}
            highlight={hasHighlightPriority}
            top={hasTopPriority}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            dataTest={dataTest}
            containerRef={containerRef}
            id={id}
            gallery={<>
                {renderBadges}
                {addElementAreaLink(renderGallery)}
            </>}
        >
            { snippet }
        </ClOfferSnippet>
    );
});
